import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleForcedLogout = () => setUser(null);
    window.addEventListener('auth-logout', handleForcedLogout);
    return () => window.removeEventListener('auth-logout', handleForcedLogout);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    // Skip /me validation if we are in an OAuth callback — AuthCallback handles auth
    if (window.location.hash.includes('token=')) {
      setLoading(false);
      return;
    }

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid
      authAPI.getMe()
        .then(res => {
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
        })
        .catch(() => {
          logout();
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await authAPI.register({ email, password, name });
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const googleLogin = async (token) => {
    const response = await authAPI.googleAuth({ token });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const pinLogin = async (restaurantId, pin) => {
    const response = await authAPI.pinLogin({ restaurant_id: restaurantId, pin });
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await authAPI.logoutUser();   // increments token_version — invalidates all sessions
    } catch (_) {
      // ignore network errors — still clear client state
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const isOwner = user?.role === 'owner';
  const isManager = user?.role === 'manager';
  const isStaff = user?.role === 'staff';
  const isSuperAdmin = user?.role === 'super_admin';
  const canManage = isOwner || isManager || isSuperAdmin;

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      googleLogin,
      pinLogin,
      logout,
      updateUser,
      isOwner,
      isManager,
      isStaff,
      isSuperAdmin,
      canManage,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
